import React, { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { GlobalResponse, Slug } from '../../models/domain/interfaces';
import { useTheme } from '@mui/material';

import { LinkWrapper, RepeaterWrapper } from './NewsArchive.style';
import { ContentWrapper } from '../../styles/shared';
import { getNews } from '../../utils/api';
import normalizeText from '../../utils/normalizeText';
import { normalizeAttributesNewsEvents } from '../../utils/event';
import { Newsa as News } from '../../models/auto-generated-strapi-types/types/Newsa';
import CardNews from '../../components/cards/card-news/CardNews';
import Repeater from '../../components/repeater/Repeater';
import { fromHumanNumberOfColumnsToRepeaterNumberOfColumns } from '../../utils/carousels';
import { useWindowSize } from 'usehooks-ts';
import Button, { ButtonStyle } from '../../components/button/Button';
import SectionHeader from '../../components/section-header/SectionHeader';
import {
  TableBody,
  TableBodyRow,
  TableBodyRowCell,
  TableHeader,
} from '../../components/table/Table';
import Pagination from '../../components/pagination/Pagination';
import { PaginationItem } from '@mui/material';
import PageSelect from '../../components/page-select/PageSelect';

import {
  Label,
  MainPagination,
  StyledHeader,
  StyledLink,
  StyledSubtitle,
  TableStyled,
} from './NewsArchive.style';
import Markdown from '../../components/markdown/Markdown';
import { useRouter } from 'next/router';
import { serialize } from 'corporate-utils';
import { Companies } from 'corporate-types';
import { getCompany } from '../../utils/company';

interface NewsArchiveProps {
  mainTitle?: string;
  subtitle?: string;
  mainTitleFontSize?: number;
  subtitleFontSize?: number;
  mainTitleMobileFontSize?: number;
  subtitleMobileFontSize?: number;
  subtitleMobileFontWeight?: number;
  subtitleFontWeight?: number;
  mainTitleMobileFontWeight?: number;
  mainTitleFontWeight?: number;
  paginationNews: {
    page: number;
    pageSize: number;
    total: number;
    pageCount: number;
  };
  news: News[];
  newsAndEventsMerged?: boolean;
  locale: string;
}

const INITIAL_ITEM_NUMBER_PER_ROW_LG = 4;
const INITIAL_ITEM_NUMBER_PER_ROW_MD = 3;
const INITIAL_ITEM_NUMBER_PER_ROW_SM = 2;
const INITIAL_ITEM_NUMBER_PER_ROW_XS = 1;
const INITIAL_ITEMS_ROWS_NUMBER = 3;

const NewsArchive: React.FC<any> = ({
  data,
  translations,
}: {
  data: NewsArchiveProps;
  translations: any;
}) => {
  const router = useRouter();

  const { width } = useWindowSize();
  const theme = useTheme();
  const [news] = React.useState(data?.news);

  const page = useMemo(
    () => data?.paginationNews?.page || 1,
    [data?.paginationNews?.page]
  );
  const pageSize = useMemo(
    () => data?.paginationNews?.pageSize || 24,
    [data?.paginationNews?.pageSize]
  );

  const onChangePage = (page: number): void => {
    router.push(createPagePath(~~page));
  };

  const createPagePath = (pageToChange: number, size?: number): string => {
    const { query, asPath, locale } = router;
    const path = asPath.split('?')[0];
    const queryCopy = {
      ...query,
    };

    delete queryCopy?.slug;

    const newQuery: any = {
      ...queryCopy,
      page: pageToChange.toString(),
    };

    if (size !== undefined && size !== null) {
      newQuery.pageSize = size;
    }

    return `/${locale}${path}?${serialize(newQuery)}`;
  };

  const onChangePageSize = (size: number): void => {
    router.push(createPagePath(1, ~~size));
  };

  const createNewsLink = (row: any): string => {
    return (row?.attributes as any)?.url;
  };

  const goToNews = (row: any): void => {
    router.push(createNewsLink(row));
  };

  const [itemsNumberPerRow, setItemsNumberPerRow] = useState<number>(
    INITIAL_ITEM_NUMBER_PER_ROW_LG
  );
  const [itemsRowsNumber, setItemsRowsNumber] = useState<number>(
    INITIAL_ITEMS_ROWS_NUMBER
  );

  useEffect(() => {
    switch (true) {
      case width < theme.breakpointsMediaQuery.values.lg &&
        width >= theme.breakpointsMediaQuery.values.md:
        if (itemsNumberPerRow !== INITIAL_ITEM_NUMBER_PER_ROW_MD) {
          setItemsNumberPerRow(INITIAL_ITEM_NUMBER_PER_ROW_MD);
          setItemsRowsNumber(INITIAL_ITEMS_ROWS_NUMBER);
        }
        break;

      case width < theme.breakpointsMediaQuery.values.sm &&
        width >= theme.breakpointsMediaQuery.values.xs:
        if (itemsNumberPerRow !== INITIAL_ITEM_NUMBER_PER_ROW_XS) {
          setItemsNumberPerRow(INITIAL_ITEM_NUMBER_PER_ROW_XS);
          setItemsRowsNumber(INITIAL_ITEMS_ROWS_NUMBER);
        }
        break;

      case width < theme.breakpointsMediaQuery.values.md &&
        width >= theme.breakpointsMediaQuery.values.sm:
        if (itemsNumberPerRow !== INITIAL_ITEM_NUMBER_PER_ROW_SM) {
          setItemsNumberPerRow(INITIAL_ITEM_NUMBER_PER_ROW_SM);
          setItemsRowsNumber(INITIAL_ITEMS_ROWS_NUMBER);
        }
        break;

      case width >= theme.breakpointsMediaQuery.values.lg:
      default:
        if (itemsNumberPerRow !== INITIAL_ITEM_NUMBER_PER_ROW_LG) {
          setItemsNumberPerRow(INITIAL_ITEM_NUMBER_PER_ROW_LG);
          setItemsRowsNumber(INITIAL_ITEMS_ROWS_NUMBER);
        }
        break;
    }
  }, [itemsNumberPerRow, theme.breakpointsMediaQuery.values, width]);

  if (news)
    return getCompany() !== Companies.SIRMAN ? (
      <ContentWrapper>
        {data?.mainTitle && (
          <StyledHeader
            fontSize={data?.mainTitleFontSize}
            fontMobileSize={data?.mainTitleMobileFontSize}
            fontWeight={data?.mainTitleFontWeight}
            fontMobileWeight={data?.mainTitleMobileFontWeight}
          >
            {data?.mainTitle}
          </StyledHeader>
        )}
        {data?.subtitle && (
          <StyledSubtitle
            fontSize={data?.subtitleFontSize}
            fontMobileSize={data?.subtitleMobileFontSize}
            fontWeight={data?.subtitleFontWeight}
            fontMobileWeight={data?.subtitleMobileFontWeight}
          >
            <Markdown>{data?.subtitle}</Markdown>
          </StyledSubtitle>
        )}
        <TableStyled>
          <TableHeader
            data={[
              translations?.pages?.newsArchive?.title,
              translations?.pages?.newsArchive?.date,
              '',
            ]}
          ></TableHeader>
          <TableBody>
            {news &&
              news?.map((row, index) => {
                return (
                  <TableBodyRow key={index} onClick={() => goToNews(row)}>
                    <TableBodyRowCell>
                      {normalizeText(row?.attributes?.title)}
                    </TableBodyRowCell>
                    <TableBodyRowCell>{row?.attributes?.date}</TableBodyRowCell>
                    <TableBodyRowCell>
                      <Link href={createNewsLink(row)} passHref>
                        <StyledLink>
                          {translations?.pages?.newsArchive?.readNews}
                        </StyledLink>
                      </Link>
                    </TableBodyRowCell>
                  </TableBodyRow>
                );
              })}
          </TableBody>
        </TableStyled>
        {news?.length > 0 && (
          <MainPagination>
            <Pagination
              count={Math.ceil(data?.paginationNews?.total / pageSize)}
              page={page || 1}
              onChangePage={onChangePage}
              renderItem={(item) => (
                <PaginationItem
                  {...item}
                  href={
                    (!item?.selected &&
                      !item?.disabled &&
                      item?.page &&
                      createPagePath(item?.page)) ||
                    null
                  }
                  onClick={(e: any) => {
                    e.preventDefault();
                    item.onClick();
                  }}
                  component={
                    (!item?.selected && !item?.disabled && 'a') || null
                  }
                />
              )}
            ></Pagination>
            <div>
              <Label>
                {
                  translations?.pages?.category?.main?.products?.pagination
                    ?.label
                }
              </Label>
              <PageSelect
                currentValue={pageSize}
                onChangePageSize={onChangePageSize}
              />
            </div>
          </MainPagination>
        )}
      </ContentWrapper>
    ) : (
      <ContentWrapper>
        {data?.mainTitle || data?.subtitle ? (
          <SectionHeader
            subtitle={data?.mainTitle ?? ''}
            title={data?.subtitle ?? ''}
            titleFontSize={data?.subtitleFontSize}
            mainTitleFontSize={data?.mainTitleFontSize}
            titleMobileFontSize={data?.subtitleMobileFontSize}
            mainTitleMobileFontSize={data?.mainTitleMobileFontSize}
            mainTitleFontWeight={data?.mainTitleFontWeight}
            mainTitleMobileFontWeight={data?.mainTitleMobileFontWeight}
            titleFontWeight={data?.subtitleFontWeight}
            titleMobileFontWeight={data?.subtitleMobileFontWeight}
          />
        ) : (
          <></>
        )}
        <RepeaterWrapper>
          <Repeater
            spacing={3}
            cols={{
              lg: fromHumanNumberOfColumnsToRepeaterNumberOfColumns(
                INITIAL_ITEM_NUMBER_PER_ROW_LG
              ),
              md: fromHumanNumberOfColumnsToRepeaterNumberOfColumns(
                INITIAL_ITEM_NUMBER_PER_ROW_MD
              ),
              sm: fromHumanNumberOfColumnsToRepeaterNumberOfColumns(
                INITIAL_ITEM_NUMBER_PER_ROW_SM
              ),
              xs: fromHumanNumberOfColumnsToRepeaterNumberOfColumns(
                INITIAL_ITEM_NUMBER_PER_ROW_XS
              ),
            }}
            props={{
              translations,
            }}
            data={news.slice(0, itemsNumberPerRow * itemsRowsNumber)}
            component={CardNews}
          />
          {news.length > itemsNumberPerRow * itemsRowsNumber ? (
            <LinkWrapper>
              {page > 1 && (
                <Button
                  text={
                    translations?.pages?.newsEvents?.pastEventsSlider
                      ?.loadNewerNews
                  }
                  styleBtn={ButtonStyle?.secondary}
                  asA={true}
                  onClick={(e) => {
                    e.preventDefault();
                    onChangePage(page - 1);
                  }}
                  href={createPagePath(page - 1)}
                />
              )}
              <Button
                text={
                  translations?.pages?.newsEvents?.pastEventsSlider?.loadNews
                }
                styleBtn={ButtonStyle?.secondary}
                onClick={() => setItemsRowsNumber(itemsRowsNumber + 1)}
              />
            </LinkWrapper>
          ) : (
            <LinkWrapper>
              {page > 1 && (
                <Button
                  text={
                    translations?.pages?.newsEvents?.pastEventsSlider
                      ?.loadNewerNews
                  }
                  styleBtn={ButtonStyle?.secondary}
                  asA={true}
                  onClick={(e) => {
                    e.preventDefault();
                    onChangePage(page - 1);
                  }}
                  href={createPagePath(page - 1)}
                />
              )}
              {page * pageSize < data?.paginationNews?.total && (
                <Button
                  asA={true}
                  text={
                    translations?.pages?.newsEvents?.pastEventsSlider
                      ?.loadOlderNews
                  }
                  styleBtn={ButtonStyle?.secondary}
                  onClick={(e) => {
                    e.preventDefault();
                    onChangePage(page + 1);
                  }}
                  href={createPagePath(page + 1)}
                />
              )}
            </LinkWrapper>
          )}
        </RepeaterWrapper>
      </ContentWrapper>
    );
  else return <></>;
};

export const getServerSideProps = async (
  section: any,
  translations: any,
  slugAnalyzed: Slug,
  globalLocale: GlobalResponse
) => {
  const news = await getNews({
    locale: slugAnalyzed?.locale,
    pageSize: slugAnalyzed?.query?.pageSize,
    sort: ['date:desc'],
    page: slugAnalyzed?.query?.page,
    datesFormat: translations?.date?.newsArchive?.format,
  });

  if (
    !(
      parseInt(slugAnalyzed?.query?.page as string, 10) >
      news?.meta?.pagination?.pageCount
    )
  ) {
    for (const newss of news.data) {
      newss.attributes.title = normalizeText(newss?.attributes?.title);
    }
    section.news =
      normalizeAttributesNewsEvents(
        news?.data,
        'news',
        translations,
        globalLocale?.attributes?.newsAndEventsMerged || false,
        slugAnalyzed.locale
      ) || [];
    section.paginationNews = news?.meta?.pagination;
    section.newsAndEventsMerged =
      globalLocale?.attributes?.newsAndEventsMerged || false;
    section.locale = slugAnalyzed.locale;
  }
};

export default NewsArchive;
